import { Star } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * @param {{ givenName: string, familyName: string, className?: string }} props
 */
export function MemberAvatar({ givenName, familyName, className = '' }) {
    const a = `${givenName.charAt(0) || ''}${familyName.charAt(0) || ''}`;
    return (
        <div
            className={`member-avatar ${className}`.trim()}
            aria-hidden
        >
            <span className="member-avatar-initials">
                {a.toLocaleUpperCase('tr-TR')}
            </span>
        </div>
    );
}

/**
 * @param {{
 *   imageUrl?: string,
 *   givenName: string,
 *   familyName: string,
 *   className?: string,
 * }} props
 */
export function MemberAvatarOrPhoto({
    imageUrl,
    givenName,
    familyName,
    className = '',
}) {
    const [failed, setFailed] = useState(false);
    const url = imageUrl?.trim();

    useEffect(() => {
        setFailed(false);
    }, [url]);

    if (!url || failed) {
        return (
            <MemberAvatar
                givenName={givenName}
                familyName={familyName}
                className={className}
            />
        );
    }
    return (
        <div
            className={`member-avatar member-avatar--photo ${className}`.trim()}
            aria-hidden
        >
            <img
                key={url}
                src={url}
                alt=""
                className="member-avatar-img"
                onError={() => setFailed(true)}
                decoding="async"
            />
        </div>
    );
}

/**
 * @param {{
 *   member: import('../data/members').ClubMember,
 *   variant?: 'directory' | 'group',
 * }} props
 */
export function MemberCard({ member, variant = 'directory' }) {
    const { t, i18n } = useTranslation();
    const isGroupList = variant === 'group';
    const dateDisplay = useMemo(
        () =>
            new Intl.DateTimeFormat(
                i18n.language?.toLowerCase().startsWith('en')
                    ? 'en-US'
                    : 'tr-TR',
                {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                },
            ),
        [i18n.language],
    );

    const m = member;
    const fullName = `${m.givenName} ${m.familyName}`.trim();
    const partnerFull =
        m.partnerGivenName && m.partnerFamilyName
            ? `${m.partnerGivenName} ${m.partnerFamilyName}`
            : null;
    const birthOk =
        !isGroupList &&
        Boolean(m.birthDate) &&
        m.birthDate.length >= 10 &&
        !Number.isNaN(new Date(`${m.birthDate}T12:00:00`).getTime());
    const joinedOk =
        !isGroupList &&
        Boolean(m.joinedAt) &&
        m.joinedAt.length >= 10 &&
        !Number.isNaN(new Date(`${m.joinedAt}T12:00:00`).getTime());
    const birth = birthOk
        ? dateDisplay.format(new Date(`${m.birthDate}T12:00:00`))
        : '—';
    const joined = joinedOk
        ? dateDisplay.format(new Date(`${m.joinedAt}T12:00:00`))
        : '—';

    const memberDetailTo = `/member-area/members/${encodeURIComponent(m.id)}`;
    const partnerDetailTo = m.partnerId
        ? `/member-area/members/${encodeURIComponent(m.partnerId)}`
        : null;
    const memberLinkState = isGroupList
        ? { fromFellowshipGroups: true }
        : undefined;

    const leaderTag = m.groupLeader ? (
        <div
            className={`member-card-leader-tag${isGroupList ? ' member-card-leader-tag--corner' : ''}`}
            role="status"
        >
            <Star
                className="member-card-leader-star"
                size={isGroupList ? 17 : 15}
                strokeWidth={2}
                fill="#e87722"
                color="#e87722"
                aria-hidden
            />
            <span>{t('memberCard.groupLeader')}</span>
        </div>
    ) : null;

    return (
        <article
            className={`member-card${m.groupLeader ? ' member-card--group-leader' : ''}${isGroupList ? ' member-card--group-list' : ''}`}
        >
            {isGroupList ? leaderTag : null}
            <div className="member-card-photos">
                <Link
                    to={memberDetailTo}
                    state={memberLinkState}
                    className="member-card-photo-link"
                    aria-label={t('memberCard.viewDetailAria', { name: fullName })}
                >
                    <MemberAvatarOrPhoto
                        key={`${m.id}-main`}
                        imageUrl={m.profilePictureUrl}
                        givenName={m.givenName}
                        familyName={m.familyName}
                    />
                </Link>
                {partnerFull ? (
                    partnerDetailTo ? (
                        <Link
                            to={partnerDetailTo}
                            state={memberLinkState}
                            className="member-card-photo-link"
                            aria-label={t('memberCard.viewPartnerDetailAria', {
                                name: partnerFull,
                            })}
                        >
                            <MemberAvatarOrPhoto
                                key={`${m.id}-partner`}
                                imageUrl={m.partnerProfilePictureUrl}
                                givenName={m.partnerGivenName ?? ''}
                                familyName={m.partnerFamilyName ?? ''}
                                className="member-avatar--partner"
                            />
                        </Link>
                    ) : (
                        <MemberAvatarOrPhoto
                            key={`${m.id}-partner`}
                            imageUrl={m.partnerProfilePictureUrl}
                            givenName={m.partnerGivenName ?? ''}
                            familyName={m.partnerFamilyName ?? ''}
                            className="member-avatar--partner"
                        />
                    )
                ) : null}
            </div>
            <div className="member-card-names">
                {!isGroupList ? leaderTag : null}
                <h2 className="member-card-name">
                    <Link
                        to={memberDetailTo}
                        state={memberLinkState}
                        className="member-card-name-link"
                    >
                        {fullName}
                    </Link>
                </h2>
                {partnerFull ? (
                    <p className="member-card-partner mb-0">
                        {partnerDetailTo ? (
                            <Link
                                to={partnerDetailTo}
                                state={memberLinkState}
                                className="member-card-partner-link text-decoration-none"
                            >
                                {partnerFull}
                            </Link>
                        ) : (
                            partnerFull
                        )}
                    </p>
                ) : null}
            </div>
            {!isGroupList ? (
                <dl className="member-card-meta">
                    <div className="member-card-meta-item">
                        <dt>{t('memberCard.birthDate')}</dt>
                        <dd>
                            {birthOk ? (
                                <time dateTime={m.birthDate}>{birth}</time>
                            ) : (
                                birth
                            )}
                        </dd>
                    </div>
                    <div className="member-card-meta-item">
                        <dt>{t('memberCard.joinedClub')}</dt>
                        <dd>
                            {joinedOk ? (
                                <time dateTime={m.joinedAt}>{joined}</time>
                            ) : (
                                joined
                            )}
                        </dd>
                    </div>
                    <div className="member-card-meta-item">
                        <dt>{t('memberCard.profession')}</dt>
                        <dd>{m.profession}</dd>
                    </div>
                    <div className="member-card-meta-item member-card-meta-item--email">
                        <dt>{t('memberCard.email')}</dt>
                        <dd>
                            {m.email?.trim() ? (
                                <a
                                    href={`mailto:${m.email.trim()}`}
                                    className="member-card-email"
                                >
                                    {m.email.trim()}
                                </a>
                            ) : (
                                '—'
                            )}
                        </dd>
                    </div>
                </dl>
            ) : null}
        </article>
    );
}
